import { useCallback } from 'react';
import {
  getEnrichedProject,
  getProject,
  useProjectBuildingGFA,
} from '@client/store/project';
import { fetchProject } from '@client/store/project/project.store';
import { getSelectedVersion } from '@client/store/ui';
import { getSelectedLifecycles } from '@features/lifecycles/lifecycle-scope.hooks';
import { useSelectedOrganizationName } from '@client/store/organization';
import { useRecipes } from '@client/store/recipe/recipe.hook';
import { Lifecycle } from '@shared/models/lifecycles.model';
import {
  exportProjectSpreadsheet,
  type ExportProjectId,
} from '../helpers/export-spreadsheet.helpers';
import { useUser } from './use-user.hooks';

/**
 * @param projectId - Optional project id. When it matches the already-loaded
 * enriched project, that snapshot is reused; otherwise the project is fetched
 * and enriched. Omitting the id always uses the current store project.
 * @returns A function that exports the project as a spreadsheet.
 */
export const useExportSpreadsheet = (): ((
  projectId?: ExportProjectId,
) => Promise<void>) => {
  const organizationName = useSelectedOrganizationName();
  // Include soft-deleted recipes so referenced recipes still resolve for export.
  const recipes = useRecipes(true);
  const user = useUser();
  const gfa = useProjectBuildingGFA();

  const handleExportSpreadsheet = useCallback(
    async (projectId?: ExportProjectId) => {
      // Read at click-time so the export matches the current scope selector.
      await exportProjectSpreadsheet({
        projectId,
        recipes,
        organizationName,
        contactPersonName: user?.name,
        storeGfa: gfa,
        selectedLifecycles: getSelectedLifecycles() ?? [Lifecycle.A1A3],
        selectedVersionId: getSelectedVersion()?.id,
        // Origin only — path comes from NODON_PARTIAL_PROJECT_PATH after resolve.
        origin: window.location.origin,
        // Wire store at call sites — not at helper module init (circular TDZ).
        resolveDeps: {
          getLoadedProject: getProject,
          fetchProjectById: fetchProject,
          enrichProject: getEnrichedProject,
        },
      });
    },
    [organizationName, recipes, user, gfa],
  );
  return handleExportSpreadsheet;
};
